"use client";

import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import type { ArtistBrowseRow } from "@/lib/data/bands/index-queries";
import { withLanguagePrefix } from "@/lib/site-locale";

const PAGE_SIZE = 48;

/**
 * ArtistsView — highlighted "Spotlight" cards on top, infinite-scroll
 * ranked list underneath. Both surfaces use the same underlying
 * ranking (`spotifyPopularity * A + recentNews * B`); the difference
 * is just visual emphasis — the top 6 get big photo cards, everyone
 * else gets the compact row card.
 */
export function ArtistsView({
  initialItems,
  initialCursor,
  highlights,
  language,
}: {
  initialItems: ArtistBrowseRow[];
  initialCursor: string | null;
  highlights: ArtistBrowseRow[];
  language: SiteLanguage;
}) {
  const copy = useMessages() as SiteCopy;

  // Skip the highlight rows from the paginated list — they already
  // live in the big-card band above, showing them twice is clutter.
  const highlightIds = new Set(highlights.map((row) => row.id));
  const [rows, setRows] = useState<ArtistBrowseRow[]>(() =>
    initialItems.filter((row) => !highlightIds.has(row.id)),
  );
  const [nextCursor, setNextCursor] = useState<string | null>(initialCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(initialCursor === null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchMore = useCallback(async () => {
    if (loadingMore || exhausted || !nextCursor) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ cursor: nextCursor, limit: String(PAGE_SIZE) });
      const response = await fetch(`/api/artists?${params.toString()}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`artists ${response.status}`);
      const payload = (await response.json()) as { items: ArtistBrowseRow[]; nextCursor: string | null };
      setRows((current) => {
        const seen = new Set(current.map((row) => row.id));
        const fresh = payload.items.filter((row) => !seen.has(row.id) && !highlightIds.has(row.id));
        return [...current, ...fresh];
      });
      setNextCursor(payload.nextCursor);
      if (!payload.nextCursor) setExhausted(true);
    } catch {
      // Silent retry on the next intersect.
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, exhausted, nextCursor, highlightIds]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || exhausted || !nextCursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchMore();
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchMore, exhausted, nextCursor]);

  return (
    <section className="space-y-10">
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">
          duudey / {copy.nav.artists}
        </p>
        <h1 className="text-4xl font-black leading-tight text-foreground sm:text-5xl">{copy.nav.artists}</h1>
        <p className="max-w-2xl pt-2 text-sm text-muted-foreground">{copy.ui.artistsIntro}</p>
      </header>

      {highlights.length > 0 ? (
        <section className="space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">{copy.ui.popular}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {highlights.map((artist) => (
              <HighlightCard key={artist.id} artist={artist} language={language} copy={copy} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {rows.map((artist) => (
            <CompactRow key={artist.id} artist={artist} language={language} />
          ))}
        </div>

        {!exhausted ? (
          <div ref={sentinelRef} className="flex justify-center py-6 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground">
            {loadingMore ? copy.ui.loadingMore : ""}
          </div>
        ) : null}
      </section>
    </section>
  );
}

function HighlightCard({
  artist,
  language,
  copy,
}: {
  artist: ArtistBrowseRow;
  language: SiteLanguage;
  copy: SiteCopy;
}) {
  return (
    <Link
      href={withLanguagePrefix(`/artists/${artist.slug}`, language)}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card/60 transition hover:border-rose-400/50"
    >
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-muted">
        {artist.imageUrl ? (
          <Image
            src={artist.imageUrl}
            alt={artist.name}
            fill
            sizes="(max-width: 768px) 100vw, 420px"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"

          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-4">
          <p className="text-lg font-black leading-tight text-white">{artist.name}</p>
          {artist.genres.length > 0 ? (
            <p className="truncate text-[10px] font-mono uppercase tracking-[0.16em] text-white/75">
              {artist.genres.slice(0, 3).join(" · ")}
            </p>
          ) : null}
          {artist.recentArticleCount > 0 ? (
            <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-rose-200">
              {artist.recentArticleCount} {copy.ui.inNewsRecently}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function CompactRow({ artist, language }: { artist: ArtistBrowseRow; language: SiteLanguage }) {
  return (
    <Link
      href={withLanguagePrefix(`/artists/${artist.slug}`, language)}
      className="group flex items-center gap-3 rounded-2xl border border-border bg-card/50 p-3 transition hover:border-rose-400/40 hover:bg-rose-400/5"
    >
      <div className="relative size-14 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
        {artist.imageUrl ? (
          <Image src={artist.imageUrl} alt={artist.name} fill sizes="56px" className="object-cover" />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-foreground group-hover:text-rose-600 dark:group-hover:text-rose-300">
          {artist.name}
        </p>
        {artist.genres.length > 0 ? (
          <p className="truncate text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
            {artist.genres.slice(0, 2).join(" · ")}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
